// Apolo IDE — HooksView (Epic 12).
//
// Modal that lists user-defined hooks and lets them be added / edited /
// deleted. Hooks run client-side in Electron (apolo/electron/hooks.cjs) via
// `apolo:hooks:run`. The Worker only persists the configuration.

import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, Plus, Sparkles, Trash2, X } from 'lucide-react';
import { apiFetch } from '../lib/api.js';
import {
  HOOK_CATEGORIES,
  HOOK_TEMPLATES,
  extractTemplateIdFromScript,
  templateToHookPayload,
} from '../lib/hookTemplates.js';
import { invalidateHooksCache } from '../lib/hooksRunner.js';
import { readHooksEnabled } from '../lib/flags/sprintFlags.js';

const EVENT_OPTIONS = [
  { value: 'pre_tool_call', label: 'Pré-tool (antes do agente chamar uma ferramenta)' },
  { value: 'post_tool_call', label: 'Pós-tool (depois de qualquer ferramenta)' },
  { value: 'stop', label: 'Stop (quando a sessão pausa)' },
  { value: 'user_prompt_submit', label: 'User prompt submit' },
];
const RUNTIME_OPTIONS = [
  { value: 'shell', label: 'shell (/bin/bash -c)' },
  { value: 'js-sandbox', label: 'js-sandbox (Node VM)' },
];

function EmptyForm() {
  return {
    event: 'pre_tool_call',
    matcher: '',
    script: '',
    runtime: 'shell',
    blocking: false,
    enabled: true,
  };
}

export default function HooksView({ open, onClose }) {
  const ref = useRef(null);
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EmptyForm());
  const [editingId, setEditingId] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [installingId, setInstallingId] = useState(null);
  // E6 (harness-library-uplift) — estado honesto do lifecycle: os hooks de
  // pre/post-tool e stop SÓ rodam com apolo_hooks_lifecycle ligado (default OFF).
  // O toggle abaixo é quem SETA a flag; o hooksRunner fica intocado (contrato §6).
  const [lifecycleOn, setLifecycleOn] = useState(() => readHooksEnabled());

  function toggleLifecycle() {
    const next = !lifecycleOn;
    try { window.localStorage.setItem('apolo_hooks_lifecycle', next ? '1' : '0'); } catch { /* private mode */ }
    setLifecycleOn(next);
  }

  // Templates currently installed — parsed from each hook's script marker.
  const installedTemplateIds = new Set(
    hooks.map((h) => extractTemplateIdFromScript(h?.script)).filter(Boolean),
  );

  async function installTemplate(template) {
    setInstallingId(template.id);
    setError(null);
    const payload = templateToHookPayload(template);
    const res = await apiFetch('/api/code-ide/hooks', {
      method: 'POST',
      body: payload,
    });
    setInstallingId(null);
    if (!res.ok) {
      setError(res.error || `falha ao instalar ${template.id}`);
      return;
    }
    invalidateHooksCache();
    refresh();
  }

  useEffect(() => {
    if (open && ref.current && !ref.current.open) ref.current.showModal();
    if (!open && ref.current && ref.current.open) ref.current.close();
  }, [open]);

  async function refresh() {
    setLoading(true);
    setError(null);
    const res = await apiFetch('/api/code-ide/hooks');
    setLoading(false);
    if (!res.ok) {
      setError(res.error || 'falha');
      return;
    }
    setHooks(res.data.hooks ?? []);
  }

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  function startEdit(hook) {
    setEditingId(hook.id);
    setForm({
      event: hook.event,
      matcher: hook.matcher || '',
      script: hook.script,
      runtime: hook.runtime,
      blocking: !!hook.blocking,
      enabled: !!hook.enabled,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EmptyForm());
  }

  async function handleSave(e) {
    e.preventDefault();
    setError(null);
    if (!form.script.trim()) {
      setError('Script obrigatório');
      return;
    }
    setSubmitting(true);
    const url = editingId
      ? `/api/code-ide/hooks/${editingId}`
      : '/api/code-ide/hooks';
    const method = editingId ? 'PUT' : 'POST';
    const res = await apiFetch(url, { method, body: form });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || 'falha');
      return;
    }
    cancelEdit();
    refresh();
  }

  async function handleDelete(id) {
    setError(null);
    const res = await apiFetch(`/api/code-ide/hooks/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      setError(res.error || 'falha');
      return;
    }
    refresh();
  }

  return (
    <dialog
      ref={ref}
      aria-labelledby="hooks-modal-title"
      onClose={onClose}
      className="rounded-xl border border-apolo-borderSubtle bg-apolo-surface1 p-0 text-apolo-textPrimary backdrop:bg-apolo-overlay"
    >
      <div className="flex w-[min(46rem,94vw)] max-h-[90vh] flex-col gap-4 overflow-auto p-5">
        <header className="flex items-center justify-between">
          <h2 id="hooks-modal-title" className="text-lg font-medium">
            Hooks
          </h2>
          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            className="rounded-md p-1 text-apolo-textTertiary transition hover:text-apolo-textPrimary"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <p className="text-xs text-apolo-textTertiary">
          Hooks são scripts rodados pelo Electron Node ao redor de eventos de tool/lifecycle.
          Exit code != 0 num pre-tool hook bloqueia a ferramenta (modo blocking).
        </p>

        {/* E6 — aviso honesto + toggle do lifecycle (a flag que faz os hooks RODAREM) */}
        <div
          data-testid="hooks-lifecycle-banner"
          className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2.5 ${
            lifecycleOn
              ? 'border-apolo-borderSubtle bg-apolo-surface2/40'
              : 'border-apolo-warning/40 bg-apolo-warning/5'
          }`}
        >
          <p className={`text-xs leading-snug ${lifecycleOn ? 'text-apolo-textSecondary' : 'text-apolo-warning'}`}>
            {lifecycleOn
              ? 'Lifecycle ligado — hooks de pre/post-tool e stop rodam no loop do agente.'
              : 'Seus hooks de pre/post-tool e stop NÃO rodam — o lifecycle está desligado (opt-in). Ligue ao lado pra ativá-los.'}
          </p>
          <button
            type="button"
            role="switch"
            aria-checked={lifecycleOn}
            aria-label={lifecycleOn ? 'Desligar lifecycle de hooks' : 'Ligar lifecycle de hooks'}
            onClick={toggleLifecycle}
            className={`relative h-5 w-9 shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing ${
              lifecycleOn ? 'bg-apolo-accent' : 'bg-apolo-surface3 border border-apolo-border'
            }`}
          >
            <span
              aria-hidden="true"
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                lifecycleOn ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {/* Templates panel — Hook Powerstack v1 */}
        <section className="flex flex-col gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface2/40 p-3">
          <button
            type="button"
            onClick={() => setShowTemplates((v) => !v)}
            className="flex items-center justify-between text-left text-sm font-medium text-apolo-textPrimary hover:text-apolo-accent transition"
            aria-expanded={showTemplates}
          >
            <span className="flex items-center gap-2">
              <Sparkles size={13} className="text-apolo-accent" aria-hidden="true" />
              Adicionar from Template
              <span className="text-[10px] font-normal text-apolo-textTertiary">
                · 12 hooks pre-curados
              </span>
            </span>
            <span className="text-xs text-apolo-textTertiary">{showTemplates ? 'fechar ▴' : 'abrir ▾'}</span>
          </button>

          {showTemplates && (
            <div className="flex flex-col gap-3 pt-1">
              {HOOK_CATEGORIES.map((cat) => (
                <div key={cat.id} className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider">
                    <span
                      className="inline-block h-1.5 w-1.5 rounded-full"
                      style={{ background: cat.color }}
                      aria-hidden="true"
                    />
                    <span style={{ color: cat.color }} className="font-semibold">
                      {cat.label}
                    </span>
                    <span className="text-apolo-textTertiary normal-case tracking-normal font-normal text-[10px]">
                      — {cat.description}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
                    {HOOK_TEMPLATES.filter((t) => t.category === cat.id).map((tpl) => {
                      const installed = installedTemplateIds.has(tpl.id);
                      const installing = installingId === tpl.id;
                      return (
                        <div
                          key={tpl.id}
                          className="flex items-start justify-between gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface1 px-2.5 py-2 text-xs"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-apolo-textPrimary truncate">
                              {tpl.title}
                            </div>
                            <div className="mt-0.5 text-[10px] text-apolo-textTertiary leading-snug">
                              {tpl.description}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-1 text-[9px] text-apolo-textTertiary">
                              <span className="rounded bg-apolo-surface2 px-1 py-0.5 font-mono">
                                {tpl.event}
                              </span>
                              {tpl.blocking && (
                                <span className="rounded bg-apolo-warning/15 px-1 py-0.5 text-apolo-warning">
                                  blocking
                                </span>
                              )}
                              {tpl.wiringStatus === 'pending' && (
                                <span className="rounded bg-apolo-info/15 px-1 py-0.5 text-apolo-info">
                                  wiring pendente
                                </span>
                              )}
                              {tpl.requires && (
                                <span
                                  title={`Requer: ${tpl.requires}`}
                                  className="rounded bg-apolo-surface2 px-1 py-0.5 truncate max-w-[14ch]"
                                >
                                  ⚙ {tpl.requires.slice(0, 18)}…
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => installTemplate(tpl)}
                            disabled={installed || installing}
                            className={
                              'shrink-0 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium transition ' +
                              (installed
                                ? 'cursor-default bg-apolo-success/15 text-apolo-success'
                                : 'bg-apolo-accent text-white hover:bg-apolo-accentHover disabled:opacity-50')
                            }
                          >
                            {installed ? (
                              <>
                                <CheckCircle2 size={10} aria-hidden="true" /> instalado
                              </>
                            ) : installing ? (
                              <>
                                <Loader2 size={10} className="animate-spin" aria-hidden="true" /> instalando
                              </>
                            ) : (
                              <>
                                <Plus size={10} aria-hidden="true" /> instalar
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              <p className="text-[10px] text-apolo-textTertiary italic">
                <code className="font-mono">user_prompt_submit</code> dispara sempre.
                <code className="font-mono">pre_tool_call</code> (bloqueia a ferramenta se a hook sair ≠ 0),{' '}
                <code className="font-mono">post_tool_call</code> e <code className="font-mono">stop</code> disparam
                no loop do agente quando <code className="font-mono">apolo_hooks_lifecycle</code> está ligado.
              </p>
            </div>
          )}
        </section>

        <form onSubmit={handleSave} className="flex flex-col gap-2 rounded-md border border-apolo-border bg-apolo-surface2 p-3 text-sm">
          <div className="flex gap-2">
            <label className="flex flex-1 flex-col gap-1 text-xs">
              <span className="text-apolo-textSecondary">Evento</span>
              <select
                value={form.event}
                onChange={(e) => setForm({ ...form, event: e.target.value })}
                className="rounded-md border border-apolo-border bg-apolo-surface1 px-2 py-1 text-xs"
              >
                {EVENT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-1 flex-col gap-1 text-xs">
              <span className="text-apolo-textSecondary">Runtime</span>
              <select
                value={form.runtime}
                onChange={(e) => setForm({ ...form, runtime: e.target.value })}
                className="rounded-md border border-apolo-border bg-apolo-surface1 px-2 py-1 text-xs"
              >
                {RUNTIME_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="flex flex-col gap-1 text-xs">
            <span className="text-apolo-textSecondary">Matcher (opcional — regex sobre tool name)</span>
            <input
              type="text"
              value={form.matcher}
              onChange={(e) => setForm({ ...form, matcher: e.target.value })}
              placeholder="^multi_edit$"
              className="rounded-md border border-apolo-border bg-apolo-surface1 px-2 py-1 text-xs"
            />
          </label>

          <label className="flex flex-col gap-1 text-xs">
            <span className="text-apolo-textSecondary">Script</span>
            <textarea
              rows={4}
              value={form.script}
              onChange={(e) => setForm({ ...form, script: e.target.value })}
              placeholder={
                form.runtime === 'shell'
                  ? 'echo "[$APOLO_TOOL] $(date)" >> ~/.apolo/hooks.log'
                  : 'return apolo.toolName.startsWith("multi") ? "ok" : "block"'
              }
              className="resize-y rounded-md border border-apolo-border bg-apolo-surface1 px-2 py-1 font-mono text-xs"
            />
          </label>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={form.blocking}
                onChange={(e) => setForm({ ...form, blocking: e.target.checked })}
              />
              blocking
            </label>
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              />
              enabled
            </label>
            <div className="ml-auto flex items-center gap-2">
              {editingId && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="rounded-md px-2 py-1 text-xs text-apolo-textSecondary transition hover:text-apolo-textPrimary"
                >
                  Cancelar
                </button>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-1 rounded-md bg-apolo-accent px-2.5 py-1 text-xs font-medium text-apolo-bg transition hover:bg-apolo-accent/90 disabled:opacity-60"
              >
                {submitting ? (
                  <Loader2 size={11} className="animate-spin" aria-hidden="true" />
                ) : (
                  <Plus size={11} aria-hidden="true" />
                )}
                {editingId ? 'Salvar' : 'Adicionar'}
              </button>
            </div>
          </div>

          {error && (
            <div role="alert" className="rounded-md border border-apolo-error/40 bg-apolo-error/10 px-2 py-1 text-xs text-apolo-error">
              {error}
            </div>
          )}
        </form>

        <ul className="flex flex-col gap-2">
          {loading && <li className="text-xs text-apolo-textTertiary">carregando…</li>}
          {!loading && hooks.length === 0 && (
            <li className="text-xs text-apolo-textTertiary">Nenhum hook configurado ainda.</li>
          )}
          {hooks.map((h) => (
            <li
              key={h.id}
              className="flex flex-col gap-1 rounded-md border border-apolo-border bg-apolo-surface2 px-3 py-2 text-xs"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="rounded-full bg-apolo-accent/15 px-1.5 text-apolo-accent">
                    {h.event}
                  </span>
                  <span className="text-apolo-textTertiary">{h.runtime}</span>
                  {h.blocking && (
                    <span className="rounded-full bg-apolo-warning/15 px-1.5 text-apolo-warning">
                      blocking
                    </span>
                  )}
                  {!h.enabled && (
                    <span className="rounded-full bg-apolo-textTertiary/15 px-1.5 text-apolo-textTertiary">
                      disabled
                    </span>
                  )}
                  {h.enabled && (
                    <CheckCircle2 size={11} className="text-apolo-success" aria-hidden="true" />
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => startEdit(h)}
                    className="rounded-md px-2 py-0.5 text-apolo-textSecondary transition hover:text-apolo-textPrimary"
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(h.id)}
                    aria-label="Deletar"
                    className="rounded-md p-1 text-apolo-textTertiary transition hover:text-apolo-error"
                  >
                    <Trash2 size={12} aria-hidden="true" />
                  </button>
                </div>
              </div>
              <pre className="overflow-x-auto whitespace-pre-wrap rounded bg-apolo-bg px-2 py-1 font-mono text-[10px] leading-relaxed text-apolo-textSecondary">
                {h.script}
              </pre>
            </li>
          ))}
        </ul>
      </div>
    </dialog>
  );
}
